import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    TextField,
    FormControlLabel,
    Switch,
    Alert,
    Grid,
    useTheme,
    alpha,
    CircularProgress,
    Tabs,
    Tab,
    Divider,
    Chip,
    Autocomplete,
} from '@mui/material';
import {
    Save as SaveIcon,
    CloudQueue as CloudIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Email as EmailIcon,
    Security as SecurityIcon,
    Settings as SettingsIcon,
    Send as SendIcon,
    Draw as DrawIcon,
    FolderShared as FolderSharedIcon,
    CreateNewFolder as CreateNewFolderIcon,
    DeleteOutline as DeleteFolderIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { hasCapability, CAP } from '../utils/permissions';
import { configurationsService, S3Config, SMTPConfig, NDSignConfig } from '../services/configurationsService';
import projectsService from '../services/projectsService';
import cfosService from '../services/cfosService';
import { ProjectType } from '../types';
import { buildFolderTree, createVirtualFolder, normalizeVirtualPath, type FolderNode } from '../lib/cfos/virtualFolders';
import PasswordVerificationModal from '../components/PasswordVerificationModal';
import AccessDenied from '../components/AccessDenied';
import { authService } from '../services/authService';
import { useUserManagementAccess } from '../hooks/useUserManagementAccess';
import { useTabSearchParam } from '../hooks/useTabSearchParam';

// ─── Tab Panel Helper ──────────────────────────────────────────
interface TabPanelProps {
    children?: React.ReactNode;
    index: string;
    value: string;
}

function TabPanel({ children, value, index }: TabPanelProps) {
    return (
        <div role="tabpanel" hidden={value !== index} style={{ flex: 1, minWidth: 0 }}>
            {value === index && <Box sx={{ p: { xs: 2, md: 4 } }}>{children}</Box>}
        </div>
    );
}

type ConfigTab = 's3' | 'smtp' | 'ndsign' | 'cfos-folders' | 'security';
const CONFIG_TABS: readonly ConfigTab[] = ['s3', 'smtp', 'ndsign', 'cfos-folders', 'security'];

const DEFAULT_SMTP_CONFIG: SMTPConfig = {
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_use_tls: true,
    smtp_from_address: '',
    smtp_transmittal_use_same: true,
    smtp_transmittal_host: '',
    smtp_transmittal_port: 587,
    smtp_transmittal_user: '',
    smtp_transmittal_password: '',
    smtp_transmittal_use_tls: true,
    smtp_transmittal_from_address: '',
};

function normalizeSmtpConfig(data: Partial<SMTPConfig> | null | undefined): SMTPConfig {
    return {
        ...DEFAULT_SMTP_CONFIG,
        ...data,
        smtp_transmittal_use_same: data?.smtp_transmittal_use_same !== false,
        smtp_transmittal_host: data?.smtp_transmittal_host || '',
        smtp_transmittal_port: data?.smtp_transmittal_port || 587,
        smtp_transmittal_user: data?.smtp_transmittal_user || '',
        smtp_transmittal_password: data?.smtp_transmittal_password || '',
        smtp_transmittal_use_tls: data?.smtp_transmittal_use_tls !== false,
        smtp_transmittal_from_address: data?.smtp_transmittal_from_address || '',
    };
}

// ─── Main Component ────────────────────────────────────────────
const GeneralConfigPage: React.FC = () => {
    const { user } = useAuth();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // Nautilus Console Design System Tokens
    const NAUTILUS = { deepOcean: '#0f2134', sand: '#ccba9b', abyssal: '#0a1622' };
    const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
    const surfaceBg = isDark ? NAUTILUS.deepOcean : '#ffffff';

    // Tab state
    const [activeTab, setActiveTab] = useTabSearchParam(CONFIG_TABS, 's3');

    // General state
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Password verification
    const [passwordVerificationOpen, setPasswordVerificationOpen] = useState(false);
    const { isVerified: passwordVerified, grantAccess } = useUserManagementAccess();

    // ─── S3 State ──────────────────────────────────
    const [s3Testing, setS3Testing] = useState(false);
    const [s3TestResult, setS3TestResult] = useState<{ message: string; success: boolean } | null>(null);
    const [s3Config, setS3Config] = useState<S3Config>({
        use_s3: false,
        bucket_name: '',
        region: '',
        access_key_id: '',
        secret_access_key: '',
        endpoint_url: ''
    });

    // ─── SMTP State ────────────────────────────────
    const [smtpTesting, setSmtpTesting] = useState(false);
    const [smtpTestResult, setSmtpTestResult] = useState<{ message: string; success: boolean } | null>(null);
    const [smtpTestEmail, setSmtpTestEmail] = useState('');
    const [smtpTestAccount, setSmtpTestAccount] = useState<'general' | 'transmittal' | null>(null);
    const [smtpConfig, setSmtpConfig] = useState<SMTPConfig>(DEFAULT_SMTP_CONFIG);

    // ─── NDSign State ──────────────────────────────
    const [ndsignTesting, setNdSignTesting] = useState(false);
    const [ndsignTestResult, setNdSignTestResult] = useState<{ message: string; success: boolean } | null>(null);
    const [ndsignConfig, setNdSignConfig] = useState<NDSignConfig>({
        base_url: 'https://nd-sign.nautilusdynamics.com',
        api_key: '',
        webhook_secret: '',
        oidc_client_secret: '',
        configured: false,
    });

    // ─── Security State (placeholder) ──────────────
    const [securityConfig, setSecurityConfig] = useState({
        session_timeout_minutes: 60,
        min_password_length: 8,
        require_2fa: false,
    });

    // ─── CFOS folder templates ─────────────────────
    type CfosJobRole = { id: number; name: string };
    type CfosTemplateFolder = { path: string; role_ids: number[] };
    const [projectTypes, setProjectTypes] = useState<ProjectType[]>([]);
    const [selectedProjectTypeId, setSelectedProjectTypeId] = useState<number | ''>('');
    const [cfosTemplateFolders, setCfosTemplateFolders] = useState<CfosTemplateFolder[]>([]);
    const [cfosJobRoles, setCfosJobRoles] = useState<CfosJobRole[]>([]);
    const [cfosFolderDraft, setCfosFolderDraft] = useState('');
    const [cfosTemplateLoading, setCfosTemplateLoading] = useState(false);
    const [cfosApplying, setCfosApplying] = useState(false);
    const [createAclShared, setCreateAclShared] = useState<number[]>([]);
    const [createAclProject, setCreateAclProject] = useState<number[]>([]);
    const [createAclSaving, setCreateAclSaving] = useState(false);
    const [createAclLoading, setCreateAclLoading] = useState(false);

    // Check verification on mount
    useEffect(() => {
        if (!passwordVerified) {
            setPasswordVerificationOpen(true);
        }
    }, [passwordVerified]);

    // Load all configs after verification
    useEffect(() => {
        if (passwordVerified) {
            loadAllConfigs();
        }
    }, [passwordVerified]);

    const loadAllConfigs = async () => {
        try {
            setLoading(true);
            const [s3Data, smtpData, ndsignData, types] = await Promise.all([
                configurationsService.getS3Config(),
                configurationsService.getSMTPConfig(),
                configurationsService.getNDSignConfig(),
                projectsService.getProjectTypes(),
            ]);
            setS3Config(s3Data);
            setSmtpConfig(normalizeSmtpConfig(smtpData));
            setNdSignConfig(ndsignData);
            setProjectTypes(types);
            if (types.length && selectedProjectTypeId === '') {
                setSelectedProjectTypeId(types[0].id);
            }
            setError(null);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to load configuration');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!passwordVerified || selectedProjectTypeId === '') return;
        let cancelled = false;
        setCfosTemplateLoading(true);
        cfosService.getFolderTemplates(Number(selectedProjectTypeId))
            .then((data) => {
                if (cancelled) return;
                setCfosTemplateFolders(data.folders);
                if (data.roles.length) setCfosJobRoles(data.roles);
            })
            .catch((err: any) => {
                if (!cancelled) {
                    setCfosTemplateFolders([]);
                    setError(err.response?.data?.error || err.message || 'Failed to load CFOS folder templates');
                }
            })
            .finally(() => {
                if (!cancelled) setCfosTemplateLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [passwordVerified, selectedProjectTypeId]);

    useEffect(() => {
        if (!passwordVerified) return;
        let cancelled = false;
        setCreateAclLoading(true);
        cfosService.getFolderCreateAcl()
            .then((data) => {
                if (cancelled) return;
                setCreateAclShared(data.shared.role_ids || []);
                setCreateAclProject(data.project.role_ids || []);
                if (data.roles.length) setCfosJobRoles(data.roles);
            })
            .catch((err: any) => {
                if (!cancelled) {
                    setError(err.response?.data?.error || err.message || 'Failed to load folder creation access');
                }
            })
            .finally(() => {
                if (!cancelled) setCreateAclLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [passwordVerified]);

    const cfosTemplatePaths = cfosTemplateFolders.map((f) => f.path);
    const cfosTemplateTree = buildFolderTree(cfosTemplatePaths);

    const addCfosTemplateFolder = (parentPath: string, name: string) => {
        const trimmed = name.trim().replace(/\\/g, '/');
        if (!trimmed) {
            setError('Enter a folder name');
            return;
        }
        // Allow nested draft paths like "correspondence/client"
        const draftPath = trimmed.includes('/')
            ? normalizeVirtualPath(trimmed.startsWith('/') ? trimmed : `/${trimmed}`)
            : null;
        const result = draftPath
            ? { path: draftPath }
            : createVirtualFolder(parentPath, trimmed, cfosTemplatePaths);
        if ('error' in result) {
            setError(result.error);
            return;
        }
        if (cfosTemplatePaths.includes(result.path)) {
            setError('That folder already exists.');
            return;
        }
        const ancestors: string[] = [];
        const parts = result.path.split('/').filter(Boolean);
        let current = '';
        for (const part of parts) {
            current += `/${part}`;
            ancestors.push(current);
        }
        setCfosTemplateFolders((currentFolders) => {
            const byPath = new Map(currentFolders.map((f) => [f.path, f]));
            for (const path of ancestors) {
                if (!byPath.has(path)) {
                    byPath.set(path, { path, role_ids: [] });
                }
            }
            return [...byPath.values()].sort((a, b) => a.path.localeCompare(b.path));
        });
        setCfosFolderDraft('');
        setError(null);
    };

    const removeCfosTemplateFolder = (path: string) => {
        const normalized = normalizeVirtualPath(path);
        setCfosTemplateFolders((currentFolders) => currentFolders.filter(
            (item) => item.path !== normalized && !item.path.startsWith(`${normalized}/`),
        ));
    };

    const setFolderRoleIds = (path: string, roleIds: number[]) => {
        setCfosTemplateFolders((currentFolders) => currentFolders.map((folder) => (
            folder.path === path ? { ...folder, role_ids: roleIds } : folder
        )));
    };

    const handleSaveCfosTemplates = async () => {
        if (selectedProjectTypeId === '') return;
        try {
            setSaving(true);
            setSuccess(null);
            setError(null);
            const saved = await cfosService.saveFolderTemplates(
                Number(selectedProjectTypeId),
                cfosTemplateFolders,
            );
            setCfosTemplateFolders(saved);
            setSuccess('CFOS default folders saved for this project type');
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Failed to save CFOS folder templates');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveCreateAcl = async () => {
        try {
            setCreateAclSaving(true);
            setSuccess(null);
            setError(null);
            const saved = await cfosService.saveFolderCreateAcl({
                shared: { role_ids: createAclShared },
                project: { role_ids: createAclProject },
            });
            setCreateAclShared(saved.shared.role_ids || []);
            setCreateAclProject(saved.project.role_ids || []);
            if (saved.roles.length) setCfosJobRoles(saved.roles);
            setSuccess('Folder creation access saved');
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Failed to save folder creation access');
        } finally {
            setCreateAclSaving(false);
        }
    };

    const handleApplyCfosTemplates = async () => {
        if (selectedProjectTypeId === '') return;
        try {
            setCfosApplying(true);
            setSuccess(null);
            setError(null);
            const result = await cfosService.applyFolderTemplates(Number(selectedProjectTypeId));
            setSuccess(
                `Added ${result.folders_added} missing folder(s) to existing projects of this type`,
            );
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Failed to apply CFOS folder templates');
        } finally {
            setCfosApplying(false);
        }
    };

    const roleOptionsById = (ids: number[]) => (
        cfosJobRoles.filter((role) => ids.includes(role.id))
    );

    const renderCfosFolderNodes = (nodes: FolderNode[], depth = 0): React.ReactNode => (
        nodes.map((node) => {
            const folder = cfosTemplateFolders.find((item) => item.path === node.path);
            const selectedRoles = roleOptionsById(folder?.role_ids || []);
            return (
                <Box key={node.path}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            pl: depth * 2,
                            py: 0.75,
                            borderBottom: `1px solid ${panelBorder}`,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Typography variant="body2" sx={{ flex: '1 1 140px', fontFamily: 'Roboto Mono, monospace' }}>
                            {node.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 1, minWidth: 120 }}>
                            {node.path}
                        </Typography>
                        <Autocomplete
                            multiple
                            size="small"
                            options={cfosJobRoles}
                            getOptionLabel={(option) => option.name}
                            value={selectedRoles}
                            onChange={(_, value) => setFolderRoleIds(node.path, value.map((role) => role.id))}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={selectedRoles.length ? undefined : 'All roles'}
                                    label="Visible to"
                                />
                            )}
                            sx={{ minWidth: 220, flex: '1 1 220px' }}
                        />
                        <Button
                            size="small"
                            color="inherit"
                            startIcon={<DeleteFolderIcon fontSize="small" />}
                            onClick={() => removeCfosTemplateFolder(node.path)}
                            sx={{ textTransform: 'none' }}
                        >
                            Remove
                        </Button>
                    </Box>
                    {renderCfosFolderNodes(node.children, depth + 1)}
                </Box>
            );
        })
    );

    // ─── S3 Handlers ───────────────────────────────
    const handleSaveS3 = async () => {
        try {
            setSaving(true);
            setSuccess(null);
            setError(null);
            await configurationsService.updateS3Config(s3Config);
            setSuccess('S3 configuration saved successfully');
            const data = await configurationsService.getS3Config();
            setS3Config(data);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to save S3 configuration');
        } finally {
            setSaving(false);
        }
    };

    const handleTestS3 = async () => {
        try {
            setS3Testing(true);
            setS3TestResult(null);
            const result = await configurationsService.testS3Connection(s3Config);
            setS3TestResult(result);
        } catch (err: any) {
            setS3TestResult({
                success: false,
                message: err.response?.data?.error || 'Connection test failed'
            });
        } finally {
            setS3Testing(false);
        }
    };

    // ─── SMTP Handlers ─────────────────────────────
    const handleSaveSMTP = async () => {
        try {
            setSaving(true);
            setSuccess(null);
            setError(null);
            await configurationsService.updateSMTPConfig(smtpConfig);
            setSuccess('SMTP configuration saved successfully');
            const data = await configurationsService.getSMTPConfig();
            setSmtpConfig(normalizeSmtpConfig(data));
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to save SMTP configuration');
        } finally {
            setSaving(false);
        }
    };

    const handleTransmittalUseSameChange = (checked: boolean) => {
        setSmtpConfig((prev) => {
            if (!checked && !prev.smtp_transmittal_host && !prev.smtp_transmittal_user) {
                return {
                    ...prev,
                    smtp_transmittal_use_same: false,
                    smtp_transmittal_host: prev.smtp_host,
                    smtp_transmittal_port: prev.smtp_port,
                    smtp_transmittal_user: prev.smtp_user,
                    smtp_transmittal_use_tls: prev.smtp_use_tls,
                    smtp_transmittal_from_address: prev.smtp_from_address,
                };
            }
            return { ...prev, smtp_transmittal_use_same: checked };
        });
    };

    const handleTestSMTP = async (account: 'general' | 'transmittal' = 'general') => {
        try {
            setSmtpTesting(true);
            setSmtpTestAccount(account);
            setSmtpTestResult(null);
            const result = await configurationsService.testSMTPConnection({
                ...smtpConfig,
                test_email: smtpTestEmail,
                account,
            });
            setSmtpTestResult(result);
        } catch (err: any) {
            setSmtpTestResult({
                success: false,
                message: err.response?.data?.error || 'SMTP test failed'
            });
        } finally {
            setSmtpTesting(false);
        }
    };

    // ─── NDSign Handlers ───────────────────────────
    const handleSaveNDSign = async () => {
        try {
            setSaving(true);
            setSuccess(null);
            setError(null);
            await configurationsService.updateNDSignConfig({
                base_url: ndsignConfig.base_url,
                api_key: ndsignConfig.api_key,
                webhook_secret: ndsignConfig.webhook_secret,
                oidc_client_secret: ndsignConfig.oidc_client_secret,
            });
            setSuccess('NDSign configuration saved successfully');
            const data = await configurationsService.getNDSignConfig();
            setNdSignConfig(data);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to save NDSign configuration');
        } finally {
            setSaving(false);
        }
    };

    const handleTestNDSign = async () => {
        try {
            setNdSignTesting(true);
            setNdSignTestResult(null);
            const result = await configurationsService.testNDSignConnection({
                base_url: ndsignConfig.base_url,
                api_key: ndsignConfig.api_key,
            });
            setNdSignTestResult(result);
        } catch (err: any) {
            setNdSignTestResult({
                success: false,
                message: err.response?.data?.error || 'NDSign connection test failed',
            });
        } finally {
            setNdSignTesting(false);
        }
    };

    // ─── Password Verification ─────────────────────
    const handlePasswordVerification = async (password: string) => {
        try {
            const response = await authService.verifyPassword(password);
            return response.data.verified;
        } catch (error) {
            return false;
        }
    };

    const handlePasswordVerified = () => {
        grantAccess();
        setPasswordVerificationOpen(false);
    };

    // ─── Shared button styles ──────────────────────
    const saveBtnSx = {
        px: 4,
        bgcolor: NAUTILUS.sand,
        color: NAUTILUS.deepOcean,
        fontWeight: 600,
        '&:hover': { bgcolor: alpha(NAUTILUS.sand, 0.85) },
        '&:disabled': { bgcolor: alpha(NAUTILUS.sand, 0.3) },
    };

    const outlinedBtnSx = {
        borderColor: panelBorder,
        color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
        '&:hover': { borderColor: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean },
    };

    // ─── Guard Screens ─────────────────────────────
    if (!hasCapability(user, CAP.GENERAL_CONFIG)) {
        return (
            <AccessDenied
                title="Access denied"
                message="You do not have permission to manage system configuration."
            />
        );
    }

    if (!passwordVerified) {
        return (
            <PasswordVerificationModal
                open={passwordVerificationOpen}
                onClose={() => setPasswordVerificationOpen(false)}
                onVerify={handlePasswordVerification}
                onVerified={handlePasswordVerified}
                title="Access User Management"
                description="Please verify your password to access General Configuration."
            />
        );
    }

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    // ─── Tab Config ────────────────────────────────
    const tabs: { key: ConfigTab; label: string; icon: React.ReactNode }[] = [
        { key: 's3', label: 'S3 / Object Storage', icon: <CloudIcon fontSize="small" /> },
        { key: 'smtp', label: 'SMTP Email', icon: <EmailIcon fontSize="small" /> },
        { key: 'ndsign', label: 'NDSign', icon: <DrawIcon fontSize="small" /> },
        { key: 'cfos-folders', label: 'CFOS folders', icon: <FolderSharedIcon fontSize="small" /> },
        { key: 'security', label: 'Security', icon: <SecurityIcon fontSize="small" /> },
    ];

    // ═══════════════════════════════════════════════
    //  RENDER
    // ═══════════════════════════════════════════════
    return (
        <Box>
            {/* Header */}
            <Box sx={{
                mb: 4,
                p: 3,
                bgcolor: surfaceBg,
                borderRadius: 1,
                border: `1px solid ${panelBorder}`,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            p: 1.5,
                            borderRadius: 1,
                            bgcolor: isDark ? alpha(NAUTILUS.sand, 0.15) : alpha(NAUTILUS.deepOcean, 0.08),
                            color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                        }}
                    >
                        <SettingsIcon fontSize="large" />
                    </Box>
                    <Box>
                        <Typography
                            variant="h3"
                            component="h1"
                            sx={{
                                fontWeight: 600,
                                mb: 0.5,
                                letterSpacing: '-0.01em',
                                color: isDark ? '#ffffff' : NAUTILUS.deepOcean,
                            }}
                        >
                            General Configuration
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{ color: isDark ? '#b0bec5' : '#546e7a' }}
                        >
                            Manage system-wide settings for storage, email, e-signature, CFOS folders, and security.
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Messages */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {/* Tabbed Content */}
            <Card sx={{ borderRadius: 1, border: `1px solid ${panelBorder}`, boxShadow: 'none', bgcolor: surfaceBg }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: 500 }}>
                    {/* Vertical Tabs */}
                    <Tabs
                        orientation="vertical"
                        value={activeTab}
                        onChange={(_, v: ConfigTab) => { setActiveTab(v); setError(null); setSuccess(null); }}
                        sx={{
                            borderRight: { md: `1px solid ${panelBorder}` },
                            borderBottom: { xs: `1px solid ${panelBorder}`, md: 'none' },
                            minWidth: { md: 220 },
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 500,
                                justifyContent: 'flex-start',
                                px: 3,
                                py: 2,
                                minHeight: 56,
                                color: isDark ? '#b0bec5' : '#546e7a',
                                '&.Mui-selected': {
                                    color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                                    fontWeight: 600,
                                    bgcolor: isDark ? alpha(NAUTILUS.sand, 0.08) : alpha(NAUTILUS.deepOcean, 0.04),
                                },
                            },
                            '& .MuiTabs-indicator': {
                                left: 0,
                                right: 'auto',
                                width: 3,
                                bgcolor: NAUTILUS.sand,
                            },
                        }}
                    >
                        {tabs.map((tab) => (
                            <Tab
                                key={tab.key}
                                value={tab.key}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                        {tab.icon}
                                        <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>
                                            {tab.label}
                                        </Typography>
                                    </Box>
                                }
                            />
                        ))}
                    </Tabs>

                    {/* ─── S3 Tab ───────────────────────── */}
                    <TabPanel value={activeTab} index="s3">
                        <Typography variant="h6" fontWeight={600} gutterBottom sx={{ color: isDark ? '#fff' : NAUTILUS.deepOcean }}>
                            S3 Bucket Configuration
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 3, color: isDark ? '#b0bec5' : '#546e7a' }}>
                            Configure AWS S3 or compatible object storage for file uploads.
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={s3Config.use_s3}
                                            onChange={(e) => setS3Config({ ...s3Config, use_s3: e.target.checked })}
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={600}>Enable S3 Storage</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Toggle between local storage and S3 object storage
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Grid>

                            {s3Config.use_s3 && (
                                <>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Bucket Name"
                                            value={s3Config.bucket_name}
                                            onChange={(e) => setS3Config({ ...s3Config, bucket_name: e.target.value })}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Region"
                                            value={s3Config.region}
                                            onChange={(e) => setS3Config({ ...s3Config, region: e.target.value })}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Access Key ID"
                                            value={s3Config.access_key_id}
                                            onChange={(e) => setS3Config({ ...s3Config, access_key_id: e.target.value })}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Secret Access Key"
                                            type="password"
                                            value={s3Config.secret_access_key}
                                            onChange={(e) => setS3Config({ ...s3Config, secret_access_key: e.target.value })}
                                            required
                                            helperText="Leave default (********) to keep unchanged"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Endpoint URL (Optional)"
                                            value={s3Config.endpoint_url}
                                            onChange={(e) => setS3Config({ ...s3Config, endpoint_url: e.target.value })}
                                            helperText="Required for S3-compatible services like MinIO"
                                        />
                                    </Grid>
                                </>
                            )}

                            <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                    <Button
                                        variant="outlined"
                                        onClick={handleTestS3}
                                        disabled={s3Testing || !s3Config.use_s3}
                                        startIcon={s3Testing ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                                        sx={outlinedBtnSx}
                                    >
                                        Test Connection
                                    </Button>
                                    {s3TestResult && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: s3TestResult.success ? 'success.main' : 'error.main' }}>
                                            {s3TestResult.success ? <CheckCircleIcon fontSize="small" /> : <ErrorIcon fontSize="small" />}
                                            <Typography variant="body2" fontWeight={500}>
                                                {s3TestResult.message}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                                <Button
                                    variant="contained"
                                    onClick={handleSaveS3}
                                    disabled={saving}
                                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                    size="large"
                                    sx={saveBtnSx}
                                >
                                    Save Configuration
                                </Button>
                            </Grid>
                        </Grid>
                    </TabPanel>

                    {/* ─── SMTP Tab ─────────────────────── */}
                    <TabPanel value={activeTab} index="smtp">
                        <Typography variant="h6" fontWeight={600} gutterBottom sx={{ color: isDark ? '#fff' : NAUTILUS.deepOcean }}>
                            SMTP Email Configuration
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 3, color: isDark ? '#b0bec5' : '#546e7a' }}>
                            Configure outgoing email for system notifications, then optionally a dedicated mailbox for transmittals.
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                                    General SMTP
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="SMTP Host"
                                    value={smtpConfig.smtp_host}
                                    onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_host: e.target.value })}
                                    placeholder="smtp.gmail.com"
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="SMTP Port"
                                    type="number"
                                    value={smtpConfig.smtp_port}
                                    onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_port: parseInt(e.target.value) || 587 })}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Username"
                                    value={smtpConfig.smtp_user}
                                    onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_user: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Password"
                                    type="password"
                                    value={smtpConfig.smtp_password}
                                    onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_password: e.target.value })}
                                    helperText="Leave default (********) to keep unchanged"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="From Address"
                                    value={smtpConfig.smtp_from_address}
                                    onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_from_address: e.target.value })}
                                    placeholder="noreply@yourdomain.com"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={smtpConfig.smtp_use_tls}
                                            onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_use_tls: e.target.checked })}
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={600}>Use TLS</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Enable STARTTLS encryption (recommended)
                                            </Typography>
                                        </Box>
                                    }
                                    sx={{ mt: 1 }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                                    Transmittal Email
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5, color: isDark ? '#b0bec5' : '#546e7a' }}>
                                    Send transmittals from a dedicated mailbox, or keep using the general SMTP account.
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={smtpConfig.smtp_transmittal_use_same}
                                            onChange={(e) => handleTransmittalUseSameChange(e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={600}>Use the same SMTP account for transmittals</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Turn off to send transmittals from a different mailbox
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Grid>

                            {!smtpConfig.smtp_transmittal_use_same && (
                                <>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Transmittal SMTP Host"
                                            value={smtpConfig.smtp_transmittal_host}
                                            onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_transmittal_host: e.target.value })}
                                            placeholder="smtp.gmail.com"
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Transmittal SMTP Port"
                                            type="number"
                                            value={smtpConfig.smtp_transmittal_port}
                                            onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_transmittal_port: parseInt(e.target.value) || 587 })}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Transmittal Username"
                                            value={smtpConfig.smtp_transmittal_user}
                                            onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_transmittal_user: e.target.value })}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Transmittal Password"
                                            type="password"
                                            value={smtpConfig.smtp_transmittal_password}
                                            onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_transmittal_password: e.target.value })}
                                            helperText="Leave default (********) to keep unchanged"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Transmittal From Address"
                                            value={smtpConfig.smtp_transmittal_from_address}
                                            onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_transmittal_from_address: e.target.value })}
                                            placeholder="transmittals@yourdomain.com"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={smtpConfig.smtp_transmittal_use_tls}
                                                    onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_transmittal_use_tls: e.target.checked })}
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Box>
                                                    <Typography variant="subtitle1" fontWeight={600}>Use TLS</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Enable STARTTLS encryption (recommended)
                                                    </Typography>
                                                </Box>
                                            }
                                            sx={{ mt: 1 }}
                                        />
                                    </Grid>
                                </>
                            )}

                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                            </Grid>

                            {/* Test Email */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Test Email Address"
                                    value={smtpTestEmail}
                                    onChange={(e) => setSmtpTestEmail(e.target.value)}
                                    placeholder="youraddress@example.com"
                                    helperText="Enter an email address to send a test message"
                                />
                            </Grid>

                            <Grid item xs={12} sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => handleTestSMTP('general')}
                                        disabled={smtpTesting || !smtpTestEmail}
                                        startIcon={smtpTesting && smtpTestAccount === 'general' ? <CircularProgress size={20} /> : <SendIcon />}
                                        sx={outlinedBtnSx}
                                    >
                                        Test General SMTP
                                    </Button>
                                    {!smtpConfig.smtp_transmittal_use_same && (
                                        <Button
                                            variant="outlined"
                                            onClick={() => handleTestSMTP('transmittal')}
                                            disabled={smtpTesting || !smtpTestEmail}
                                            startIcon={smtpTesting && smtpTestAccount === 'transmittal' ? <CircularProgress size={20} /> : <SendIcon />}
                                            sx={outlinedBtnSx}
                                        >
                                            Test Transmittal SMTP
                                        </Button>
                                    )}
                                    {smtpTestResult && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: smtpTestResult.success ? 'success.main' : 'error.main' }}>
                                            {smtpTestResult.success ? <CheckCircleIcon fontSize="small" /> : <ErrorIcon fontSize="small" />}
                                            <Typography variant="body2" fontWeight={500}>
                                                {smtpTestResult.message}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                                <Button
                                    variant="contained"
                                    onClick={handleSaveSMTP}
                                    disabled={saving}
                                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                    size="large"
                                    sx={saveBtnSx}
                                >
                                    Save Configuration
                                </Button>
                            </Grid>
                        </Grid>
                    </TabPanel>

                    {/* ─── NDSign Tab ───────────────────── */}
                    <TabPanel value={activeTab} index="ndsign">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                            <Typography variant="h6" fontWeight={600} sx={{ color: isDark ? '#fff' : NAUTILUS.deepOcean }}>
                                NDSign E-Signature
                            </Typography>
                            <Chip
                                label={ndsignConfig.configured ? 'Configured' : 'Not configured'}
                                size="small"
                                sx={{
                                    bgcolor: ndsignConfig.configured
                                        ? (isDark ? alpha('#4caf50', 0.18) : alpha('#2e7d32', 0.1))
                                        : (isDark ? alpha(NAUTILUS.sand, 0.15) : alpha(NAUTILUS.deepOcean, 0.08)),
                                    color: ndsignConfig.configured
                                        ? (isDark ? '#81c784' : '#2e7d32')
                                        : (isDark ? NAUTILUS.sand : NAUTILUS.deepOcean),
                                    fontWeight: 600,
                                    fontSize: '0.7rem',
                                }}
                            />
                        </Box>
                        <Typography variant="body2" sx={{ mb: 3, color: isDark ? '#b0bec5' : '#546e7a' }}>
                            Service credentials used by PDF Editor to create signing envelopes. Leave a secret as ******** to keep the current value.
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="NDSign Base URL"
                                    value={ndsignConfig.base_url}
                                    onChange={(e) => setNdSignConfig({ ...ndsignConfig, base_url: e.target.value })}
                                    placeholder="https://nd-sign.nautilusdynamics.com"
                                    required
                                    helperText="Public URL of the NDSign service"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="API Key"
                                    type="password"
                                    value={ndsignConfig.api_key}
                                    onChange={(e) => setNdSignConfig({ ...ndsignConfig, api_key: e.target.value })}
                                    required
                                    helperText="Service API key sent as X-API-Key. Leave ******** unchanged."
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Webhook Secret"
                                    type="password"
                                    value={ndsignConfig.webhook_secret}
                                    onChange={(e) => setNdSignConfig({ ...ndsignConfig, webhook_secret: e.target.value })}
                                    helperText="Shared secret for envelope status webhooks. Leave ******** unchanged."
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="OIDC Client Secret"
                                    type="password"
                                    value={ndsignConfig.oidc_client_secret}
                                    onChange={(e) => setNdSignConfig({ ...ndsignConfig, oidc_client_secret: e.target.value })}
                                    helperText="Must match NDSign's OIDC_CLIENT_SECRET for staff SSO (client ndsign-web). Leave ******** unchanged."
                                />
                            </Grid>

                            <Grid item xs={12} sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                    <Button
                                        variant="outlined"
                                        onClick={handleTestNDSign}
                                        disabled={ndsignTesting}
                                        startIcon={ndsignTesting ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                                        sx={outlinedBtnSx}
                                    >
                                        Test Connection
                                    </Button>
                                    {ndsignTestResult && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: ndsignTestResult.success ? 'success.main' : 'error.main' }}>
                                            {ndsignTestResult.success ? <CheckCircleIcon fontSize="small" /> : <ErrorIcon fontSize="small" />}
                                            <Typography variant="body2" fontWeight={500}>
                                                {ndsignTestResult.message}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                                <Button
                                    variant="contained"
                                    onClick={handleSaveNDSign}
                                    disabled={saving}
                                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                    size="large"
                                    sx={saveBtnSx}
                                >
                                    Save Configuration
                                </Button>
                            </Grid>
                        </Grid>
                    </TabPanel>

                    {/* ─── CFOS folders Tab ─────────── */}
                    <TabPanel value={activeTab} index="cfos-folders">
                        <Typography variant="h6" fontWeight={600} gutterBottom sx={{ color: isDark ? '#fff' : NAUTILUS.deepOcean }}>
                            CFOS default folders
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 3, color: isDark ? '#b0bec5' : '#546e7a' }}>
                            Define the default folder tree created in each new project&apos;s CFOS sandbox for a given project type.
                            Restrict visibility per folder by job role (empty = all CFOS users). Nested folders stay hidden unless
                            the user can also access every parent. Existing projects are unchanged unless you apply missing folders below.
                        </Typography>

                        <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ color: isDark ? '#fff' : NAUTILUS.deepOcean }}>
                            Who can create folders
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2, color: isDark ? '#b0bec5' : '#546e7a' }}>
                            Leave empty to allow all CFOS users. Applies to the Create folder action (including empty folders from drag-and-drop).
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    multiple
                                    size="small"
                                    loading={createAclLoading}
                                    options={cfosJobRoles}
                                    getOptionLabel={(option) => option.name}
                                    value={roleOptionsById(createAclShared)}
                                    onChange={(_, value) => setCreateAclShared(value.map((role) => role.id))}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Shared workspace"
                                            placeholder={createAclShared.length ? undefined : 'All roles'}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    multiple
                                    size="small"
                                    loading={createAclLoading}
                                    options={cfosJobRoles}
                                    getOptionLabel={(option) => option.name}
                                    value={roleOptionsById(createAclProject)}
                                    onChange={(_, value) => setCreateAclProject(value.map((role) => role.id))}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Project sandboxes"
                                            placeholder={createAclProject.length ? undefined : 'All roles'}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    variant="outlined"
                                    onClick={handleSaveCreateAcl}
                                    disabled={createAclSaving}
                                    startIcon={createAclSaving ? <CircularProgress size={18} /> : <SaveIcon />}
                                    sx={{ textTransform: 'none', fontWeight: 600 }}
                                >
                                    Save folder creation access
                                </Button>
                            </Grid>
                        </Grid>

                        <Divider sx={{ mb: 3 }} />

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Project type"
                                    value={selectedProjectTypeId}
                                    onChange={(e) => setSelectedProjectTypeId(e.target.value === '' ? '' : Number(e.target.value))}
                                    SelectProps={{ native: true }}
                                >
                                    {projectTypes.map((type) => (
                                        <option key={type.id} value={type.id}>
                                            {type.description} ({type.project_type})
                                        </option>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <TextField
                                        size="small"
                                        label="New root folder"
                                        placeholder="correspondence"
                                        value={cfosFolderDraft}
                                        onChange={(e) => setCfosFolderDraft(e.target.value)}
                                        sx={{ minWidth: 240 }}
                                    />
                                    <Button
                                        variant="outlined"
                                        startIcon={<CreateNewFolderIcon />}
                                        disabled={!cfosFolderDraft.trim() || selectedProjectTypeId === ''}
                                        onClick={() => addCfosTemplateFolder('/', cfosFolderDraft)}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Add folder
                                    </Button>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Tip: to nest, add a folder using a slash path like <code>correspondence/client</code> in the field
                                    (or add children by typing the full relative path from workspace root).
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        border: `1px solid ${panelBorder}`,
                                        borderRadius: 1,
                                        minHeight: 180,
                                        bgcolor: isDark ? alpha('#000', 0.2) : alpha(NAUTILUS.deepOcean, 0.02),
                                        p: 1,
                                    }}
                                >
                                    {cfosTemplateLoading ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                            <CircularProgress size={28} />
                                        </Box>
                                    ) : cfosTemplateTree.length ? (
                                        renderCfosFolderNodes(cfosTemplateTree)
                                    ) : (
                                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                            No default folders for this project type yet.
                                        </Typography>
                                    )}
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Leave &quot;Visible to&quot; empty for all roles. Nested folders require access to every parent template folder.
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Button
                                    variant="contained"
                                    onClick={handleSaveCfosTemplates}
                                    disabled={saving || selectedProjectTypeId === ''}
                                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                    size="large"
                                    sx={saveBtnSx}
                                >
                                    Save defaults
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleApplyCfosTemplates}
                                    disabled={cfosApplying || selectedProjectTypeId === ''}
                                    startIcon={cfosApplying ? <CircularProgress size={18} /> : <FolderSharedIcon />}
                                    sx={{ textTransform: 'none', fontWeight: 600 }}
                                >
                                    Add missing folders to existing projects
                                </Button>
                            </Grid>
                        </Grid>
                    </TabPanel>

                    {/* ─── Security Tab (placeholder) ──── */}
                    <TabPanel value={activeTab} index="security">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                            <Typography variant="h6" fontWeight={600} sx={{ color: isDark ? '#fff' : NAUTILUS.deepOcean }}>
                                Security & Authentication
                            </Typography>
                            <Chip label="Coming Soon" size="small" sx={{ bgcolor: isDark ? alpha(NAUTILUS.sand, 0.15) : alpha(NAUTILUS.deepOcean, 0.08), color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean, fontWeight: 600, fontSize: '0.7rem' }} />
                        </Box>
                        <Typography variant="body2" sx={{ mb: 3, color: isDark ? '#b0bec5' : '#546e7a' }}>
                            Configure session policies, password requirements, and two-factor authentication.
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Session Timeout (minutes)"
                                    type="number"
                                    value={securityConfig.session_timeout_minutes}
                                    onChange={(e) => setSecurityConfig({ ...securityConfig, session_timeout_minutes: parseInt(e.target.value) || 60 })}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Minimum Password Length"
                                    type="number"
                                    value={securityConfig.min_password_length}
                                    onChange={(e) => setSecurityConfig({ ...securityConfig, min_password_length: parseInt(e.target.value) || 8 })}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={securityConfig.require_2fa}
                                            onChange={(e) => setSecurityConfig({ ...securityConfig, require_2fa: e.target.checked })}
                                            color="primary"
                                            disabled
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={600}>Require Two-Factor Authentication</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Enforce 2FA for all users upon login
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 2 }}>
                                <Alert severity="info" sx={{ bgcolor: isDark ? alpha(NAUTILUS.sand, 0.05) : undefined }}>
                                    Security configuration is planned for a future release. These fields are read-only for now.
                                </Alert>
                            </Grid>
                        </Grid>
                    </TabPanel>
                </Box>
            </Card>
        </Box>
    );
};

export default GeneralConfigPage;
